/**
 * GLSL of the Medium tier's one merged lens pass: the shared prototype
 * engine's lens (`engine/src/focus.js`, `createFocusShader`) with the slot
 * bodies the dungeon gives it (`dungeon-floors/src/dungeon-focus.js`): a
 * screen-space tilt band that follows the leader, eight depth-aware taps
 * rotated per pixel by interleaved gradient noise, dust motes in the air, an
 * edge haze, film grain and a luminance-scaled dither; then the grade (zones,
 * lamp warmth, shadow tint with a lift and chroma floor, highlights, contrast
 * above mid-grey, saturation, gain, vignette). Output tone mapping (ACES) and
 * the sRGB conversion end the same pass (three's OutputPass merged in).
 *
 * `focusFragment(slots)` composes the template once at build time; every slot
 * is a named function argument, never a string patch afterwards. Left out
 * because the dungeon does not use them: the town's canal water mask, its sun
 * glare and its outside-the-town haze bounds.
 *
 * `zoneGround` differs from the engine: its `baseH` is Lanternwater's terrace
 * height by position (0 where world z > -4.8, else 2.4 west of x 7 and 3.36
 * east of it), and the prototype dungeon inherited it. In dungeon coordinates
 * that faded the cool and dim zones on rock above 1.6 in the five southern
 * rows (y 0 to 4) only, and nowhere else (the fade starts 1.6 above `baseH`,
 * higher than any dungeon rock). The game uses one height, 2.4, everywhere, so
 * rock in those five rows keeps the full mine-shade zone (a little cooler and
 * dimmer than the prototype there); elsewhere the two agree.
 */
import { Color } from 'three';

export type FocusSlots = {
  /** Extra uniforms and helpers before `main`. */
  readonly declarations: string;
  /** The ground's height under a world point (`world`): where the light zones' cool and dim fade with height. */
  readonly zoneGround: string;
  /** Haze added for what lies beyond the walkable place (an expression; the town's, `0.` in the dungeon). */
  readonly outsideHaze: string;
  /** Before the zone-map block, and before the final gain and lift (the engine lens's slots). */
  readonly beforeZones: string;
  readonly beforeGrade: string;
  /** First statements of `main`. */
  readonly start: string;
  /** Must define `float blur`. */
  readonly blur: string;
  /** Opens the gather; must define `vec3 sum` and `float weights`. */
  readonly gatherOpen: string;
  readonly taps: number;
  /** Must define `float a`. */
  readonly sampleAngle: string;
  /** Must define `vec2 p`. */
  readonly tap: string;
  /** Must define `float weight`. */
  readonly sampleWeight: string;
  /** Must write `color`. */
  readonly gatherClose: string;
  readonly beforeVignette: string;
  readonly afterVignette: string;
  readonly grain: string;
};

export const focusFragment = (s: FocusSlots): string => {
  if (!Number.isInteger(s.taps) || s.taps < 1) throw new Error('lens: taps must be a positive integer');
  return `
#include <packing>
uniform sampler2D tDiffuse;uniform sampler2D tDepth;uniform sampler2D zoneMap;
uniform vec2 resolution;uniform float time,focusDistance,cameraNear,cameraFar,strength,night;
uniform vec3 gradeShadow,gradeHighlight,gradeLift,gradeGain,gradeVignette,hazeColor;
uniform float gradeSaturation,gradeContrast,hazeAmount,zoneStrength,zoneGround;
uniform vec2 hazeRange,projScale;uniform vec4 zoneRect;uniform mat4 viewToWorld;
uniform float shadowChroma;uniform vec3 shadowLift;
uniform vec3 zoneMood;uniform float waterMask,glare;uniform vec2 waterBand,sunUv;
varying vec2 vUv;
float depthAt(vec2 p){return -perspectiveDepthToViewZ(unpackRGBAToDepth(texture2D(tDepth,p)),cameraNear,cameraFar);}
float hash(vec2 p){return fract(sin(dot(p,vec2(12.9898,78.233)))*43758.5453);}
float luma(vec3 c){return dot(c,vec3(.2126,.7152,.0722));}
${s.declarations}
void main(){
  vec2 uv=vUv;vec3 color=texture2D(tDiffuse,uv).rgb;
  ${s.start}
  float d=depthAt(uv);
  if(strength>0.){
    ${s.blur}
    ${s.gatherOpen}
    for(int i=0;i<${s.taps};i++){
      ${s.sampleAngle}float radius=sqrt((float(i)+.5)/${s.taps}.);
      ${s.tap}
      float sampleDepth=depthAt(p);
      ${s.sampleWeight}
      sum+=texture2D(tDiffuse,p).rgb*weight;weights+=weight;
    }
    ${s.gatherClose}
  }
  ${s.beforeZones}
  vec2 ndc=uv*2.-1.;
  vec3 viewPos=vec3(ndc*d/projScale,-d);
  vec3 world=(viewToWorld*vec4(viewPos,1.)).xyz;
  vec4 zoneTex=texture2D(zoneMap,(world.xz-zoneRect.xy)/zoneRect.zw);
  float atGround=1.-smoothstep(1.6,3.,world.y-(${s.zoneGround}));
  vec3 zone=(zoneTex.rgb-.5)*zoneStrength;
  zone=max(zone,0.)+min(zone,0.)*atGround+zoneMood;
  float wm=waterMask*(1.-smoothstep(waterBand.x,waterBand.y,world.y));
  float L0=luma(color);
  zone.r*=zone.r>0.?smoothstep(.0,.1,L0):1.;
  color*=max(1.+zone.g*1.7,.2);
  color*=max(1.+zone.r*vec3(.85,.18,-.75),.2);
  float lamp=zoneTex.a*night;
  color=color*(1.+lamp*vec3(1.3,.62,.16))+lamp*lamp*vec3(.02,.008,.001);
  float L=max(luma(color),1e-4);
  float sh=1.-smoothstep(.005,.18,L);
  float hi=smoothstep(.1,.8,L);
  float cool=smoothstep(.0,.12,(color.b-color.r)/L*.5);
  float leaf=smoothstep(.0,.3,(color.g-max(color.r,color.b))/L);
  float shw=sh*(1.-.6*cool)*(1.-.6*leaf)*(1.-min(lamp*1.5,1.));
  vec3 chroma=color/max(max(color.r,max(color.g,color.b)),1e-4);
  color=mix(color,color*gradeShadow,shw*.35)+shw*(shadowLift+chroma*shadowChroma);
  color*=mix(vec3(1.),gradeHighlight,hi*(1.-.8*cool)*(1.-.75*leaf));
  L=max(luma(color),1e-4);
  float Lc=L<.18?L:.18*pow(L/.18,gradeContrast);
  color*=Lc/L;L=Lc;
  color=max(mix(vec3(L),color,gradeSaturation+zone.r*.15+leaf*.25),0.);
  color.r*=1.-leaf*.08;
  float hz=${s.outsideHaze}+smoothstep(hazeRange.x,hazeRange.y,d)*hazeAmount*.25+max(zone.b,0.)*.55;
  hz=clamp(hz,0.,.85)*(1.-.8*wm);
  color=mix(color,hazeColor*(.55+.45*min(L0*2.,1.)),hz);
  ${s.beforeGrade}
  color=color*gradeGain+gradeLift*(1.-.7*wm);
  vec3 teal=vec3(min(color.r,color.g*.7),max(color.g,color.b*.96),color.b);
  color=mix(color,teal,wm*.85*(1.-night));
  if(glare>0.){
    vec2 asp=vec2(resolution.x/resolution.y,1.);
    vec2 toSun=(uv-sunUv)*asp;float r=length(toSun);
    vec3 veil=vec3(1.,.6,.36)*exp(-r*4.)*.3+vec3(1.,.55,.5)*exp(-r*1.4)*.035;
    vec2 axis=(vec2(.5)-sunUv);vec3 ghosts=vec3(0.);
    for(int i=0;i<3;i++){
      float k=.55+float(i)*.32;vec2 c=sunUv+axis*k*1.35;
      float g=length((uv-c)*asp);float rad=.035+float(i)*.03;
      float ring=smoothstep(rad,rad*.55,g)*(.5+.5*smoothstep(rad*.4,rad*.9,g));
      ghosts+=ring*(i==1?vec3(.55,.45,1.):vec3(1.,.6,.4))*(.05-float(i)*.012);
    }
    color+=(veil+ghosts)*glare;
  }
  float vignette=1.-smoothstep(.27,.86,length((uv-.5)*vec2(.9,1.)));
  ${s.beforeVignette}
  color*=mix(gradeVignette,vec3(1.03),vignette);
  ${s.afterVignette}
  ${s.grain}
  gl_FragColor=vec4(color,1.);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;
};

/** Floating dust: two parallax layers of hashed cells in screen space, offset by the camera so motes stay put in the air. */
const motes = `
uniform vec2 focusBand,tiltScale;uniform vec4 tiltRamp,edgeHaze;uniform float blurPx,grain,dust;uniform vec3 edgeHazeTop,edgeHazeBottom;
float dungeonIgn(vec2 p){return fract(52.9829189*fract(dot(p,vec2(.06711056,.00583715))));}
float dungeonMotes(vec2 uv,float soft){
  vec3 cam=viewToWorld[3].xyz;float aspect=resolution.x/resolution.y,m=0.;
  for(int l=0;l<2;l++){
    float fl=float(l),cells=15.+fl*11.;
    vec2 p=vec2(uv.x*aspect,uv.y)*cells+vec2(cam.x,-cam.z*.8)*cells*(.10+fl*.05)+vec2(fl*17.3,time*(.035+fl*.02));
    vec2 id=floor(p),f=fract(p);
    float h=hash(id+fl*31.7);
    if(h>.16+fl*.05)continue;
    vec2 c=.5+.3*vec2(sin(time*.37+h*40.),cos(time*.29+h*61.));
    float px=resolution.y/cells,dist=length(f-c)*px,size=mix(.75+fl*.25,2.6,soft);
    float tw=.55+.45*sin(time*(1.1+h*3.)+h*90.);
    m+=(1.-smoothstep(size*.35,size,dist))*tw*mix(1.,.45,soft)*(.6+fl*.4);
  }
  return m;
}`;

/** The dungeon's slot bodies (`dungeon-focus.js`). */
export const dungeonSlots = (taps: number): FocusSlots => ({
  declarations: motes,
  zoneGround: 'zoneGround',
  outsideHaze: '0.',
  beforeZones: '',
  beforeGrade: '',
  start: 'float dungeonBand=0.;',
  blur: 'float above=uv.y-focusBand.x; float band=max(smoothstep(tiltRamp.x,tiltRamp.y,above)*tiltScale.x,smoothstep(tiltRamp.z,tiltRamp.w,-above)*tiltScale.y)*focusBand.y; dungeonBand=band; float blur=max(band,smoothstep(7.,16.,abs(d-focusDistance)))*strength;',
  gatherOpen: 'if(blur>0.){float spin=dungeonIgn(gl_FragCoord.xy)*6.2831853;vec3 sum=color;float weights=1.;',
  taps,
  sampleAngle: 'float a=float(i)*2.399963+spin;',
  tap: 'vec2 p=uv+vec2(cos(a),sin(a))*radius*blur*blurPx/resolution;',
  sampleWeight: 'float weight=1.-smoothstep(.5,3.,abs(sampleDepth-d));',
  gatherClose: 'color=sum/weights;}',
  beforeVignette: 'if(dust>0.)color+=dust*dungeonMotes(uv,clamp(dungeonBand,0.,1.))*smoothstep(.025,.14,luma(color))*(1.-smoothstep(focusDistance+3.,focusDistance+9.,d))*vec3(.62,.74,1.);',
  afterVignette: '{float hy=uv.y-.5,he=smoothstep(edgeHaze.x,.5,abs(hy));color+=(hy>0.?edgeHazeTop:edgeHazeBottom)*he*he*edgeHaze.y;}',
  grain: 'float g=hash(gl_FragCoord.xy+floor(time*12.))-.5;color*=1.+g*grain*(1.-.5*clamp(dungeonBand,0.,1.));color+=(dungeonIgn(gl_FragCoord.xy)-.5)*.0025*clamp(luma(color)/.03,.15,1.);',
});

export const fullscreenVertex = 'varying vec2 vUv;void main(){vUv=uv;gl_Position=vec4(position.xy,0.,1.);}';

/** The depth snapshot: the beauty pass's depth, packed into RGBA8 at half resolution for the lens, coronas and shafts. */
export const snapshotFragment = `#include <packing>
uniform sampler2D sourceDepth;varying vec2 vUv;
void main(){gl_FragColor=packDepthToRGBA(texture2D(sourceDepth,vUv).x);}`;

/**
 * The town's slot bodies (the shared engine's `focus.js` defaults, slice 6a):
 * a 16-tap depth-of-field focus on the traveler, fine grain, the terraces'
 * heights as the zone ground (`ground`, an expression of `world`), and haze
 * for what lies beyond the walkable rectangle (`haze`: centre and half extent
 * in world x and z). The contact occlusion is not a slot: it is composited
 * into the image before bloom and the lens (`aoCompositeFragment`), so every
 * tap the focus gathers is already occluded, as in the prototype's chain.
 */
export const townSlots = (options: { readonly taps: number; readonly tapPixels: number; readonly focus: readonly [number, number]; readonly ground: string; readonly haze: readonly [number, number, number, number] }): FocusSlots => {
  const [cx, cz, hx, hz] = options.haze.map((v) => v.toFixed(3));
  return {
    declarations: '',
    zoneGround: options.ground,
    outsideHaze: `smoothstep(3.,34.,max(abs(world.x-(${cx}))-${hx},abs(world.z-(${cz}))-${hz}))*hazeAmount*1.15`,
    beforeZones: '',
    beforeGrade: '',
    start: '',
    blur: `float blur=smoothstep(${options.focus[0].toFixed(3)},${options.focus[1].toFixed(3)},abs(d-focusDistance))*strength;`,
    gatherOpen: 'vec3 sum=color;float weights=1.;',
    taps: options.taps,
    sampleAngle: 'float a=float(i)*2.399963;',
    tap: `vec2 p=uv+vec2(cos(a),sin(a))*radius*blur*${options.tapPixels.toFixed(3)}/resolution;`,
    sampleWeight: 'float weight=1.-smoothstep(.5,3.,abs(sampleDepth-d));',
    gatherClose: 'color=sum/weights;',
    beforeVignette: '',
    afterVignette: '',
    grain: 'color+=(hash(gl_FragCoord.xy+floor(time*8.))-.5)*.004;',
  };
};

/**
 * The airship's slot bodies (ADR 0033, brad-airship `airship-focus.js`): ten
 * normalised disk taps that keep the full blur radius, a gather skipped where
 * the blur is under 1% (a sub-pixel disk), a focus band wider below deck
 * (`airshipIndoor`), the far layer (the procedural sky at the far plane: its
 * own soft blur when the lens is off, and a whisper of the time of day's tint,
 * `farTint`, `farMix`, `farBlur`), and the valley haze the prototype computes
 * but never applies in the sky (kept as written). Its uniforms are
 * `shipLensUniforms` (the lens's `uniforms` option). The tone mapping is the
 * template's (the prototype folded its OutputPass into this pass too).
 */
export const shipSlots = (): FocusSlots => ({
  declarations: 'uniform float airshipIndoor,farMix,farBlur;uniform vec3 farTint;',
  zoneGround: '0.',
  outsideHaze: '0.',
  start: '',
  blur: 'float blur=smoothstep(mix(1.6,3.8,airshipIndoor),mix(10.,17.,airshipIndoor),abs(d-focusDistance))*strength;',
  gatherOpen: 'vec3 sum=color;float weights=1.;if(blur>.01){',
  taps: 10,
  sampleAngle: 'float a=float(i)*2.399963;',
  tap: 'vec2 p=uv+vec2(cos(a),sin(a))*radius*blur*mix(mix(8.,5.5,airshipIndoor),max(18.,15./max(strength,.001))*farBlur,step(cameraFar-.5,d))/resolution;',
  sampleWeight: 'float weight=1.-smoothstep(.5,3.,abs(sampleDepth-d));',
  gatherClose: '}color=sum/weights;',
  beforeZones: `if(strength<=0.&&airshipIndoor<.5&&d>cameraFar-.5){
    vec3 farSum=color;float farWeight=1.;
    for(int i=0;i<8;i++){
      float a=float(i)*2.399963;vec2 p=uv+vec2(cos(a),sin(a))*sqrt((float(i)+.5)/8.)*15.*farBlur/resolution;
      float w=step(cameraFar-.5,depthAt(p));farSum+=texture2D(tDiffuse,p).rgb*w;farWeight+=w;
    }
    color=farSum/farWeight;
  }
  if(airshipIndoor<.5&&d>cameraFar-.5){
    color=mix(color,farTint*(.6+.4*min(luma(color)*2.,1.)),farMix);
  }`,
  beforeGrade: `float valley=(1.-exp(-max(0.,d-28.)*.012))*smoothstep(1.,19.,-world.y);
  valley*=.26+.055*sin(world.y*.22+world.x*.035);
  valley*=1.-step(cameraFar-.5,d);`,
  beforeVignette: '',
  afterVignette: '',
  grain: 'color+=(hash(gl_FragCoord.xy+floor(time*8.))-.5)*.004;',
});

/** The airship lens's own uniforms (`airship-focus.js`): below deck or not, and the far layer's tint, mix and blur. */
export const shipLensUniforms = (): { readonly airshipIndoor: { value: number }; readonly farTint: { value: Color }; readonly farMix: { value: number }; readonly farBlur: { value: number } } => ({
  airshipIndoor: { value: 0 },
  farTint: { value: new Color() },
  farMix: { value: 0 },
  farBlur: { value: 1 },
});

/**
 * The contact occlusion's composite (`engine/src/occlusion.js`, its third
 * pass): the beauty image times the half-resolution AO, upsampled bilinearly
 * with depth-aware weights from the full-resolution depth, sparing bright
 * flames and windows; written to its own target, which bloom and the lens
 * then read (the prototype's order: occlusion, bloom, lens).
 */
export const aoCompositeFragment = `#include <packing>
uniform sampler2D tDepth,tAO,tDiffuse;uniform float cameraNear,cameraFar,intensity;uniform vec2 resolution;varying vec2 vUv;
float distanceAt(vec2 uv){return -perspectiveDepthToViewZ(texture2D(tDepth,uv).r,cameraNear,cameraFar);}
void main(){
  vec3 color=texture2D(tDiffuse,vUv).rgb;
  float center=distanceAt(vUv),sum=0.,weight=0.;
  vec2 grid=vUv*resolution-.5,base=floor(grid),f=fract(grid);
  for(int y=0;y<2;y++)for(int x=0;x<2;x++){
    vec2 p=(base+vec2(float(x),float(y))+.5)/resolution;
    float w=(x==0?1.-f.x:f.x)*(y==0?1.-f.y:f.y);
    w*=exp(-abs(distanceAt(p)-center)*28.);
    sum+=texture2D(tAO,p).r*w;weight+=w;
  }
  float ao=weight>.001?sum/weight:1.;
  float highlight=smoothstep(.9,2.5,max(color.r,max(color.g,color.b)));
  color*=mix(1.,ao,intensity*(1.-highlight*.8));
  gl_FragColor=vec4(color,1.);
}`;

/** A probe of the composite (tests): the composite over the beauty image at one point, halved (red), and the beauty's brightest channel, halved (green). */
export const aoProbeFragment = `uniform sampler2D tBeauty,tComposite;uniform vec2 at;varying vec2 vUv;
void main(){
  vec3 b=texture2D(tBeauty,at).rgb,c=texture2D(tComposite,at).rgb;
  float m=max(b.r,max(b.g,b.b));
  gl_FragColor=vec4(clamp(max(c.r,max(c.g,c.b))/max(m,1e-5)*.5,0.,1.),clamp(m*.5,0.,1.),0.,1.);
}`;

/** The contact occlusion's bilateral denoise (`engine/src/occlusion.js`): twelve depth-weighted taps of the raw AO at half resolution. */
export const aoDenoiseFragment = `#include <packing>
uniform sampler2D tDepth,tAO;uniform float cameraNear,cameraFar;uniform vec2 resolution;varying vec2 vUv;
float distanceAt(vec2 uv){return -perspectiveDepthToViewZ(texture2D(tDepth,uv).r,cameraNear,cameraFar);}
void main(){
  float center=distanceAt(vUv),sum=texture2D(tAO,vUv).r,weight=1.;
  for(int i=0;i<12;i++){
    float angle=float(i)*2.399963;
    vec2 p=vUv+vec2(cos(angle),sin(angle))*sqrt((float(i)+1.)/12.)*2.4/resolution;
    float w=exp(-abs(distanceAt(p)-center)*18.);
    sum+=texture2D(tAO,p).r*w;weight+=w;
  }
  gl_FragColor=vec4(vec3(sum/weight),1.);
}`;
